import type { VendorWorkType } from '../types';
import {
  RFQ_EMAIL_SIGNATURE_NAME,
  RFQ_PUBLISH_LOGO_URL,
  rfqProjectEmailDescriptor,
  rfqProjectEmailLabel,
} from './rfqPublishEmailPreview';

function rejectionBidCopy(workType: VendorWorkType): { noun: string; heading: string; subject: string } {
  if (workType === 'cnc_quote') {
    return { noun: 'CNC bid', heading: 'CNC bid update', subject: 'CNC bid update' };
  }
  if (workType === 'other_quote') {
    return { noun: 'quote', heading: 'Quote update', subject: 'Quote update' };
  }
  return { noun: 'timber bid', heading: 'Timber bid update', subject: 'Timber bid update' };
}

const PORTAL_URL = 'https://clients.lctimberframes.com/vendor-portal';
const DEFAULT_COMPANY = 'LC Timber Frames';

export type RfqQuoteRejectionEmailContext = {
  recipientFirstName?: string;
  vendorOrgName?: string;
  senderName?: string;
  dealTitle: string;
  projectNumber?: string;
  projectName?: string;
  workType: VendorWorkType;
  customMessage?: string;
  quoteLabel: string;
  rejectionReason: string;
  companyName?: string;
  portalUrl?: string;
  logoUrl?: string;
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function messageHtml(customMessage: string | undefined, fallbackHtml: string): string {
  const trimmed = (customMessage || '').trim();
  if (!trimmed) return fallbackHtml;
  const inner = trimmed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
  return `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">${inner}</p>`;
}

function buildSenderSignature(ctx: RfqQuoteRejectionEmailContext): string {
  const sender = (ctx.senderName || '').trim();
  const organization = escapeHtml(RFQ_EMAIL_SIGNATURE_NAME);
  if (!sender) {
    return `
    <p style="color: #4F423A; margin: 28px 0 0; font-size: 14px; line-height: 1.6;">
      Best regards,<br/>
      <strong>${organization}</strong>
    </p>
  `.trim();
  }
  return `
    <p style="color: #4F423A; margin: 28px 0 0; font-size: 14px; line-height: 1.6;">
      Best regards,<br/>
      <strong>${escapeHtml(sender)}</strong><br/>
      <span style="color: #6B6960;">${organization}</span>
    </p>
  `.trim();
}

export function defaultRfqQuoteRejectionEmailMessage(ctx: {
  recipientFirstName?: string;
  quoteLabel: string;
  workType: VendorWorkType;
  rejectionReason: string;
}): string {
  const first = (ctx.recipientFirstName || '').trim() || 'there';
  const quote = ctx.quoteLabel.trim() || 'your bid';
  const reason = ctx.rejectionReason.trim();
  const reasonBlock = reason ? `\n\n${reason}` : '';
  const { noun } = rejectionBidCopy(ctx.workType);
  return (
    `Hi ${first},\n\n` +
    `Thank you for submitting your ${noun} (${quote}). We appreciate the time you invested in this quote.\n\n` +
    `After careful review, we have selected another supplier for this project.${reasonBlock}\n\n` +
    `We hope to work with you on future opportunities. Active RFQs remain available in the vendor portal.`
  );
}

export function personalizeRfqQuoteRejectionEmailMessage(
  customMessage: string,
  ctx: {
    recipientFirstName?: string;
    quoteLabel: string;
    workType: VendorWorkType;
    rejectionReason: string;
  },
): string {
  const trimmed = customMessage.trim();
  if (!trimmed) return trimmed;
  const first = (ctx.recipientFirstName || '').trim() || 'there';
  let msg = trimmed.replace(/^Hi\s+[^,\n]+,/i, `Hi ${first},`);
  const quote = ctx.quoteLabel.trim() || 'your bid';
  const { noun } = rejectionBidCopy(ctx.workType);
  const escapedNoun = noun.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  msg = msg.replace(new RegExp(`your ${escapedNoun} \\([^)]+\\)`, 'i'), `your ${noun} (${quote})`);
  return msg;
}

export function rfqQuoteRejectionEmailSubject(
  projectNumber: string | undefined,
  projectDescriptor: string | undefined,
  workType: VendorWorkType,
): string {
  const title = rfqProjectEmailLabel(projectNumber, projectDescriptor);
  return `${rejectionBidCopy(workType).subject}: ${title}`;
}

function buildProjectDetailsBlock(ctx: RfqQuoteRejectionEmailContext): string {
  const projectLabel = escapeHtml(
    rfqProjectEmailLabel(
      ctx.projectNumber,
      rfqProjectEmailDescriptor({ descriptor: ctx.projectName, dealTitle: ctx.dealTitle }),
    ),
  );
  const quoteBlock = ctx.quoteLabel.trim()
    ? `<p style="margin: 12px 0 0; font-size: 14px; color: #4F423A; line-height: 1.5;">
        <strong>Your bid:</strong> ${escapeHtml(ctx.quoteLabel.trim())}
      </p>`
    : '';
  const reasonBlock = ctx.rejectionReason.trim()
    ? `<p style="margin: 12px 0 0; font-size: 14px; color: #4F423A; line-height: 1.5;">
        <strong>Reason:</strong> ${escapeHtml(ctx.rejectionReason.trim())}
      </p>`
    : '';

  return `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="margin: 28px 0 20px; border-collapse: separate; border-spacing: 0;">
      <tr>
        <td style="background: #F7F5F0; border: 1px solid #E2E0DB; border-radius: 8px; padding: 16px 20px;">
          <p style="margin: 0 0 8px; font-size: 13px; color: #6B6960; text-transform: uppercase; letter-spacing: 0.04em;">Project</p>
          <p style="margin: 0; font-size: 16px; font-weight: 650; color: #2C3E2F;">${projectLabel}</p>
          ${quoteBlock}
          ${reasonBlock}
        </td>
      </tr>
    </table>
  `.trim();
}

export function buildRfqQuoteRejectionEmailHtml(ctx: RfqQuoteRejectionEmailContext): string {
  const companyNameRaw = ctx.companyName?.trim() || DEFAULT_COMPANY;
  const companyName = escapeHtml(companyNameRaw);
  const portalUrl = ctx.portalUrl?.trim() || PORTAL_URL;
  const logoUrl = ctx.logoUrl?.trim() || RFQ_PUBLISH_LOGO_URL;
  const vendorHeader = escapeHtml((ctx.vendorOrgName || '').trim() || 'Vendor Portal');
  const first = escapeHtml((ctx.recipientFirstName || '').trim() || 'there');
  const quoteEscaped = escapeHtml(ctx.quoteLabel.trim() || 'your bid');
  const reasonEscaped = escapeHtml(ctx.rejectionReason.trim());
  const { noun, heading } = rejectionBidCopy(ctx.workType);
  const nounEscaped = escapeHtml(noun);

  const fallbackBody = `<p style="color: #4F423A; line-height: 1.65; margin: 0 0 16px; font-size: 14px;">
        Hi ${first},<br/><br/>
        Thank you for submitting your ${nounEscaped} (${quoteEscaped}). We appreciate the time you invested in this quote.<br/><br/>
        After careful review, we have selected another supplier for this project.${reasonEscaped ? `<br/><br/>${reasonEscaped}` : ''}<br/><br/>
        We hope to work with you on future opportunities. Active RFQs remain available in the vendor portal.
      </p>`;

  const body = messageHtml(
    personalizeRfqQuoteRejectionEmailMessage(ctx.customMessage || '', ctx),
    fallbackBody,
  );

  const bodyRows = `
          <tr>
            <td style="background: #2C3E2F; padding: 28px 24px 22px; border-radius: 10px 10px 0 0; text-align: center;">
              <img src="${escapeHtml(logoUrl)}" alt="${companyName}" width="160" style="display:block;margin:0 auto 10px;border:0;width:160px;height:auto;max-width:160px;" />
              <p style="color: #C8D4CA; margin: 0 0 4px; font-size: 12px; font-style: italic; letter-spacing: 0.01em; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">Partnering with</p>
              <p style="color: #FFFFFF; margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.02em; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">${vendorHeader}</p>
            </td>
          </tr>
          <tr>
            <td style="background: #FFFFFF; padding: 28px 24px; border: 1px solid #E2E0DB; border-top: none; border-radius: 0 0 10px 10px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <p style="color: #1A1A1A; font-size: 17px; font-weight: 650; margin: 0 0 16px;">${heading}</p>
              ${body}
              ${buildProjectDetailsBlock(ctx)}
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin: 0 0 4px;">
                <tr>
                  <td style="border-radius: 6px; background: #2C3E2F;">
                    <a href="${portalUrl}" style="display: inline-block; padding: 14px 32px; background: #2C3E2F; color: #FFFFFF; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 14px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">Open Vendor Portal</a>
                  </td>
                </tr>
              </table>
              ${buildSenderSignature(ctx)}
            </td>
          </tr>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(heading)}</title>
</head>
<body style="margin:0;padding:0;background-color:#F7F5F0;-webkit-text-size-adjust:100%;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F7F5F0;">
    <tr>
      <td align="center" style="padding:24px 16px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="560" style="max-width:560px;width:100%;border-collapse:collapse;">
          ${bodyRows}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`.trim();
}
